import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DateTimePicker } from '@/components/sessions/DateTimePicker';
import { usePositionsStore } from '@/store/positionsStore';
import { useAuth } from '@/providers/AuthProvider';
import type { PageProps } from '@/types/page';
import type { CreatePositionRequest } from '@arrowise/shared';
import { ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CreatePositionPage({ nav = true, footer = true }: PageProps) {
    const navigate = useNavigate();
    const { createPosition } = usePositionsStore();
    const { teams, getUserTeamRole } = useAuth();
    const [loading, setLoading] = useState(false);

    // Determine available teams for selection
    const availableTeams = teams?.filter(team => {
        const role = getUserTeamRole(team.team.id);
        return role === 'owner' || role === 'admin';
    }) || [];

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        hiringEndDate: null as Date | null,
        teamId: availableTeams.length > 0 ? availableTeams[0].team.id : undefined
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Basic validation
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Position name is required';
        }
        if (formData.name.length > 120) {
            newErrors.name = 'Position name must be 120 characters or less';
        }
        if (formData.description && formData.description.length > 2000) {
            newErrors.description = 'Description must be 2000 characters or less';
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            return;
        }

        setLoading(true);
        try {
            const payload: CreatePositionRequest = {
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                hiringEndDate: formData.hiringEndDate?.toISOString() || undefined,
                teamId: formData.teamId
            };

            await createPosition(payload);
            navigate('/app/positions');
        } catch (error: any) {
            // Error handling is done in the store
        } finally {
            setLoading(false);
        }
    };

    const updateFormData = (updates: Partial<typeof formData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
        // Clear errors for updated fields
        const updatedFields = Object.keys(updates);
        setErrors(prev => {
            const newErrors = { ...prev };
            updatedFields.forEach(field => delete newErrors[field]);
            return newErrors;
        });
    };

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-2xl w-full space-y-8">
                <div className="flex items-center gap-4">
                    <Link to="/app/positions">
                        <Button variant="outline" size="sm">
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Create Position</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Define a new job position for your interview sessions.
                        </p>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Position Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="name">Position Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => updateFormData({ name: e.target.value })}
                                    placeholder="e.g. Senior Software Engineer"
                                    maxLength={120}
                                />
                                {errors.name && (
                                    <p className="text-sm text-destructive">{errors.name}</p>
                                )}
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => updateFormData({ description: e.target.value })}
                                    placeholder="Describe the role, requirements, and responsibilities..."
                                    rows={4}
                                    maxLength={2000}
                                />
                                {errors.description && (
                                    <p className="text-sm text-destructive">{errors.description}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                    {formData.description.length}/2000 characters
                                </p>
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label>Hiring End Date</Label>
                                <DateTimePicker
                                    value={formData.hiringEndDate}
                                    onChange={(hiringEndDate) => updateFormData({ hiringEndDate })}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Optional: Set a date when hiring for this position will end
                                </p>
                            </div>

                            <div className="flex items-center gap-2 pt-4">
                                <Button type="submit" disabled={loading}>
                                    {loading ? 'Creating...' : 'Create Position'}
                                </Button>
                                <Link to="/app/positions">
                                    <Button type="button" variant="outline">
                                        Cancel
                                    </Button>
                                </Link>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </PageLayout>
    );
}
